import { Component, ElementRef, Injector, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, ResolveFn, Router } from '@angular/router';
import { ErrorParser, Platform } from '@app/classes';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { PendingPayrollStatus } from '@app/interfaces/pending-payroll-status.interface';
import { Office } from '@app/models/company/office.model';
import { LegacyTaxCredit } from '@app/models/employee/payroll/legacy-tax-credit.model';
import { CompanyAdditionalIncome } from '@app/models/payroll/company-additional-income.model';
import { EmployeeAdditionalIncome } from '@app/models/payroll/employee-additional-income.model';
import { CUSTOM_DATA_GROUP_DIALOG } from '@app/modules/common/views/show/show.view';
import { DataFieldGroupValuesComponent } from '@app/modules/employees/components/data-field-group-values/data-field-group-values.component';
import { DataFieldGroupsComponent } from '@app/modules/employees/components/data-field-groups/data-field-groups.component';
import { getDataFieldGroupByName } from '@app/modules/employees/functions/get-data-field-group-by-name';
import { CompensationHistoryReadOnlyTable } from '@app/modules/employees/services/tables/compensation-history/compensation-history-read-only.table';
import { CompensationHistoryTable } from '@app/modules/employees/services/tables/compensation-history/compensation-history.table';
import { JobAndPayWarningProvider } from '@app/modules/employees/warning-providers/job-and-pay.warning-provider';
import { DataFieldsForm, EmploymentInformationForm, SalaryForm } from '@app/modules/forms/employees/components';
import { BankAccountForm } from '@app/modules/forms/employees/components/bank-account/bank-account.form';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AbilityService, AnalyticService, PayrollService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { FileHelperService } from '@app/services/file-helper.service';
import { NotifyService } from '@app/services/notify.service';
import { ChangeRequestTemplateDisplayOptions } from '@app/types/translatables/change-request-template-display.options';
import { EmploymentTypeDisplayOptions } from '@app/types/translatables/employment-type-display.options';
import { PaymentMethodDisplayOptions } from '@app/types/translatables/payment-method-display.options';
import { ShowView } from '@common/views';
import { EmployeesResolver } from '@employees/employees.resolver';
import { EmployeeChangeRequestTemplatesService } from '@employees/services';
import { BaseForm } from '@forms/base.form';
import { ChangeRequestTemplate } from '@models/change-requests/change-request-template.model';
import { BankAccount } from '@models/employee/bank-account.model';
import { DataValue } from '@models/employee/data-value.model';
import { Employee } from '@models/employee/employee.model';
import { Salary } from '@models/employee/salary.model';
import { PayrollEmployee } from '@models/payroll/payroll-employee.model';
import { CompanySetting } from '@models/settings/company-setting.model';
import { DataField } from '@models/settings/data-field.model';
import { DefaultGroupFieldNamesEnum } from '@settings/enums/default-group-field-names.enum';
import moment from 'moment';
import { TaxCreditTableLoadEvent } from '../tax-credit/tax-credit-table/tax-credit-table.component';

type JobAndPayShowDialogType = 'EMPLOYMENT_INFO' | 'BANKING_INFO' | CUSTOM_DATA_GROUP_DIALOG;

@Component({
    templateUrl: './job-and-pay-show.template.html',
    styleUrls: ['./job-and-pay-show.style.scss'],
})
export class JobAndPayShowView extends ShowView implements OnInit {
    @ViewChild('confirmDeleteSalary', { static: true }) confirmDeleteSalary: DeprecatedConfirmationDialogComponent;
    @ViewChild('confirmDeleteTaxCredit', { static: true })
    confirmDeleteTaxCredit: DeprecatedConfirmationDialogComponent;
    @ViewChild('confirmEnablePayroll', { static: true }) confirmEnablePayroll: DeprecatedConfirmationDialogComponent;
    @ViewChild('editSalaryModal', { static: true }) editSalaryModal: DeprecatedModalFormComponent;
    @ViewChild('formEditSalary', { static: true }) formEditSalary: SalaryForm;
    @ViewChild('formSalary', { static: true }) formSalaryVC: SalaryForm;
    @ViewChild('formBankAccount') formBankAccount: BankAccountForm;
    @ViewChild('formEmploymentInformation') formEmploymentInformation?: EmploymentInformationForm;
    @ViewChild('formEmploymentInformationCustom') formEmploymentInformationCustom?: DataFieldsForm;
    @ViewChildren(DataFieldGroupValuesComponent)
    dataFieldGroupValuesComponents?: QueryList<DataFieldGroupValuesComponent>;
    @ViewChildren(DataFieldGroupsComponent) dataFieldGroupsComponents?: QueryList<DataFieldGroupsComponent>;

    isSelfEmployeeView = false;
    isLoading = true;
    employee: Employee;
    employmentTypesDisplay = EmploymentTypeDisplayOptions;
    salary: Salary;
    bankAccount: BankAccount;
    offices: Office[] = [];
    dataValues: DataValue[];
    dataFields: DataField[];
    editableDataFields: DataField[];
    payrollEmployee?: PayrollEmployee;
    jobAndPayWarningProvider: JobAndPayWarningProvider;
    activeCompanyAdditionalIncomes: CompanyAdditionalIncome[] = [];
    viewEmploymentPermission: string[] = ['view.employeeEmploymentDetail'];
    viewCompensationPermission: string[] = Salary.permission.some('view');
    viewBankAccountPermission: string[] = BankAccount.permission.some('view');
    viewPayrollPermission: string[] = Platform.payroll.permission.view;
    payrollEnabled = false;

    editEmploymentPermission: string[] = [
        'create.employeeEmploymentDetail',
        'update.employeeEmploymentDetail',
        'delete.employeeEmploymentDetail',
    ];
    editBankAccountPermission: string[] = BankAccount.permission.edit;
    canEditSalary = false;
    canDeleteSalary = false;
    canEditDataValues = false;
    resetAdditionalIncomeModal = false;
    bankingInformationRequiredByDefault = false;
    pendingPayrollExists = false;
    employeeIsOnboarding = false;
    table: CompensationHistoryTable;
    employeeCurrentSalaries: Salary[] = [];
    isBankAccountLoaded = false;
    isCurrentCompensationsLoaded = false;
    isDataFieldsLoaded = false;
    displayCompensationTable = false;
    readOnlyTable: CompensationHistoryReadOnlyTable;
    canCreateCompensation = false;
    employeeVacationPayMethod = false;

    defaultGroupFieldNames = DefaultGroupFieldNamesEnum;
    getDataFieldGroupByName = getDataFieldGroupByName;

    canShowEditEmploymentInformation = false;
    canShowEditBankAccount = false;

    taxCreditUIImprovementsEnabled = false;

    hasChangeRequestFeatureFlag = false;
    hasBox45FeatureFlag = false;
    changeRequestTemplates: ChangeRequestTemplate[] = [];
    canCreateChangeRequest = false;

    displayEmployeeLevelVacationPayModal = false;
    displayEditVacationPayMethodModal = false;
    hasEditVacationPayMethodPermission = false;
    changeRequestTemplate = ChangeRequestTemplateDisplayOptions;
    paymentMethodDisplay = PaymentMethodDisplayOptions;

    currentlyOpenDialog?: JobAndPayShowDialogType = undefined;

    protected resolver: {
        resolve: ResolveFn<any>;
    };
    private _employeeHasHadVacationPay: boolean;
    private _updatedTaxCreditsExist?: boolean;
    protected isQcSupported: boolean;

    constructor(
        public changeRequestTemplateService: EmployeeChangeRequestTemplatesService,
        protected abilities: AbilityService,
        protected element: ElementRef,
        protected route: ActivatedRoute,
        protected router: Router,
        protected auth: AuthService,
        protected file: FileHelperService,
        protected notify: NotifyService,
        protected injector: Injector,
        protected payrollService: PayrollService,
        protected analyticsService: AnalyticService,
        protected featureService: FeatureService
    ) {
        super();
    }

    handleTaxCreditDataLoad(event: TaxCreditTableLoadEvent): void {
        this._updatedTaxCreditsExist = !event.isEmpty;
    }

    get hasRqFilled(): boolean {
        const company = this.auth.company;

        return Boolean(
            company.quebecEnterpriseNumber ||
                company.rqRegistration ||
                company.rqProgramCode ||
                company.rqReferenceNumber ||
                company.rqRemittanceFrequency
        );
    }

    get shouldShowLegacyTaxCredits(): boolean {
        // If the FF is off, we should always display the legacy credits
        if (!this.taxCreditUIImprovementsEnabled) {
            return true;
        }
        // If the FF is on and new tax credits have been created, we always hide the legacy credits
        // We also hide when _updatedTaxCreditsExist is undefined because that means it is still loading
        if (this._updatedTaxCreditsExist !== false) {
            return false;
        }
        // If the FF is on and there are no new tax credits, only display the legacy credits if they exist
        return Boolean(
            this.payrollEmployee?.taxInformation?.taxCredits?.length ||
                Number(this.payrollEmployee.taxInformation.additionalTaxDeducted)
        );
    }

    get employeeHasHadVacationPay(): boolean {
        const dataNotLoadedOrMissing = !this.employee || !this.employee.salaries || !this.employee.salaries.length;

        if (dataNotLoadedOrMissing) {
            return false;
        }

        if (this._employeeHasHadVacationPay === undefined) {
            this._employeeHasHadVacationPay = Boolean(
                this.employee.salaries.find((salary) => salary.hasVacationPayPercentage)
            );
        }

        return this._employeeHasHadVacationPay;
    }

    get showVacationPay(): boolean {
        return this.employee.is(this.auth.employee) || this.auth.isAdmin();
    }

    get showAddIncomeTypeButton(): boolean {
        if (this.pendingPayrollExists) {
            return false;
        }

        return this.activeCompanyAdditionalIncomes.some(
            ({ employeeAdditionalIncomes }) => !employeeAdditionalIncomes[0]?.active
        );
    }

    get showEditIncomeTypeButton(): boolean {
        return this.activeCompanyAdditionalIncomes.some(
            ({ employeeAdditionalIncomes }) => employeeAdditionalIncomes[0]?.active
        );
    }

    get unusedCompanyAdditionalIncomes(): CompanyAdditionalIncome[] {
        const unusedAdditionalIncomes = this.activeCompanyAdditionalIncomes
            .filter(({ employeeAdditionalIncomes }) => !employeeAdditionalIncomes[0]?.active)
            .filter((employeeAdditionalIncome: CompanyAdditionalIncome) => {
                return employeeAdditionalIncome.recurrable;
            });

        return unusedAdditionalIncomes;
    }

    get cannotAddToPayroll(): boolean {
        return Boolean(
            !this.hasRqFilled && !this.employee.activeOnPayroll() && this.employee.office?.province === 'QC'
        );
    }

    isUserProfile(): boolean {
        return this.employee.id === this.auth.user.id;
    }

    async ngOnInit(): Promise<void> {
        this.payrollEnabled = this.abilities.payroll();
        this.setResolver();
        this.employee = this.route.snapshot.data.models.get('employee');
        this.employeeIsOnboarding = this.employee.isOnboarding();
        await this.getFeatureFlags();

        this.isLoading = false;

        // These calls omit `await` in order to optimize page load performance.
        this.setSalary();
        this.getPayrollInformation();
        this.setOfficesAndBankAccount();
        if (this.hasChangeRequestFeatureFlag) {
            this.getChangeRequestTemplates();
        }

        Promise.all([this.setDataValues(), this.setDataFields()]).then(() => {
            this.setCanEditDataValues();
            this.isDataFieldsLoaded = true;
        });

        this.hasEditVacationPayMethodPermission = this.setHasEditVacationPayMethodPermission();

        if (this.payrollEnabled && this.employee.activeOnPayroll() && this.auth.can(Platform.payroll.permission.view)) {
            await this.setAdditionalIncomes();
        }
        await this.getEmployeeBankingInformation();
        await this.getEmployeeCurrentSalaries();
        this.determineIfCompensationCanBeCreated();
        this.canShowEditEmploymentInformation = this.canShowEditModalForDefaultGroup(
            this.defaultGroupFieldNames.employment_information
        );
        this.canShowEditBankAccount = this.canShowEditModalForDefaultGroup(this.defaultGroupFieldNames.bank_account);

        this.isLoading = false;
    }

    shouldEditVacationPayMethodModalBeDisplayed(displayEditVacationPayMethodModal: boolean): void {
        this.displayEditVacationPayMethodModal = displayEditVacationPayMethodModal;
    }

    determineIfCompensationCanBeCreated(): void {
        if (this.canEditSalary) {
            this.canCreateCompensation = true;
        }
    }

    async getEmployeeBankingInformation(): Promise<void> {
        try {
            const [companySettings] = await CompanySetting.param('company', this.auth.company.id)
                .where('module', 'EmployeeManagement')
                .with('setting')
                .get();
            this.bankingInformationRequiredByDefault = companySettings.find(
                (setting) => setting.setting.name === 'bankingInformation.required'
            ).value;
        } catch (error) {
            this.notify.error(error.message || 'Could not load settings');
        }
    }

    async getEmployeeCurrentSalaries(): Promise<void> {
        if (this.auth.can(Salary.permission.view) || this.isUserProfile()) {
            [this.employeeCurrentSalaries] = await Salary.param('company', this.auth.company.id)
                .param('employee', this.employee.id)
                .where('current', true)
                .get();
            this.isCurrentCompensationsLoaded = true;
        }
    }

    async getFeatureFlags(): Promise<void> {
        const [
            employeeVacationPayMethod,
            hasChangeRequestFeatureFlag,
            taxCreditUIImprovements,
            isQcSupported,
            hasBox45FeatureFlag,
        ] = await this.featureService.hasMany(
            FeatureFlag.employeeVacationPayMethod,
            FeatureFlag.changeRequests,
            FeatureFlag.taxCreditUIImprovements,
            FeatureFlag.qcPayroll,
            FeatureFlag.bulkUpdateBox45
        );
        this.employeeVacationPayMethod = employeeVacationPayMethod;
        this.hasChangeRequestFeatureFlag = hasChangeRequestFeatureFlag;
        this.taxCreditUIImprovementsEnabled = taxCreditUIImprovements;
        this.isQcSupported = isQcSupported;
        this.hasBox45FeatureFlag = hasBox45FeatureFlag;
    }

    async reResolve(): Promise<void> {
        this.isLoading = true;
        this.displayCompensationTable = false;
        try {
            const models = await this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot);
            this.employee = <Employee>models.get('employee');
            await this.getPayrollInformation();
            await this.setDataValues(true);
            await this.setSalary();
            await this.setOfficesAndBankAccount();
            this.setManager();
            await this.getEmployeeCurrentSalaries();
            this.isLoading = false;
        } catch (error) {
            this.notify.error(ErrorParser.parse(error));
            this.isLoading = false;
        }
    }

    submitAndValidate(form: BaseForm): boolean {
        if (form) {
            form.submit();
            return form.valid;
        }
        return true;
    }

    async onSave(form?: BaseForm): Promise<void> {
        this.isLoading = true;

        try {
            // @jira BUG-2135
            // We have to save bank account first, synchronously, to ensure parallel
            // calls to payroll do not patch old persisted database values
            if (this.bankAccount?.isDirty()) {
                await this.bankAccount
                    .param('company', this.auth.company.id)
                    .param('employee', this.employee.id)
                    .save();

                this.isSelfEmployeeView
                    ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileJobAndPayTabEditBankAccount)
                    : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileJobAndPayTabBankAccountSave);
            }

            if (this.payrollEmployee) {
                this.employee.taxInformation = this.payrollEmployee.taxInformation;
            }

            if (this.employee?.isDirty()) {
                await this.employee.param('company', this.auth.company.id).save();

                // Pendo Tracking
                if (!this.isSelfEmployeeView) {
                    this.addPendoTrackingForEmployemntInformationModal();
                }
            }

            await Promise.all(this.saveDataFields());

            await this.reResolve();
            this.notify.success('saved');
            this.closeDialog();
        } catch (e) {
            this.notify.error(e.message);
            this.isLoading = false;
            if (form) {
                form.addExternalError();
            }
        }
    }

    async onSaveEmployeeVacationPayMethod(
        employeeVacationPayMethod: string,
        employeeVacationPayMethodForm: BaseForm
    ): Promise<void> {
        if (!this.submitAndValidate(employeeVacationPayMethodForm)) {
            return;
        }
        try {
            this.employee.vacationPayMethod = employeeVacationPayMethod;
            await this.employee.save();
            this.notify.success('employees.jobAndPay.vacationPayMethodUpdatedSuccessfully');
        } catch (error) {
            this.notify.error(ErrorParser.parse(error));
        }
        this.shouldEditVacationPayMethodModalBeDisplayed(false);
    }

    /**
     * TODO: refactor so this method is not duplicated in personal-show.view
     */
    saveDataFields(): Promise<any>[] {
        return this.dataValues
            .filter((dataValue: DataValue) => {
                if (dataValue.isDirty()) {
                    return true;
                }

                const dataField = this.dataFields.find(
                    (currentField: DataField) => Number(currentField.id) === Number(dataValue.dataFieldId)
                );

                if (!dataField) {
                    return false;
                }

                // we are saving a data value that is not dirty
                // because it's required AND hasn't been saved yet
                // PDT-1475
                const shouldPersistPristineValue = dataField.isRequired && !dataValue.isPersisted;
                return shouldPersistPristineValue;
            })
            .map((dataValue) => dataValue.param('company', this.auth.company.id).save());
    }

    async onDataFieldsSave(form?: BaseForm): Promise<void> {
        const dataValuePromises = this.saveDataFields();

        try {
            await Promise.all(dataValuePromises);
            if (this.isSelfEmployeeView) {
                this.analyticsService.trackEvent(AnalyticEvents.SelfProfileJobAndPayTabEditCustomField);
            }
            await this.reResolve();
            this.notify.success('saved');
            this.closeDialog();
        } catch (error) {
            this.notify.error(ErrorParser.parse(error || 'couldNotSaveData'));
            form?.addExternalError();
        }
    }

    onCancel(): void {
        if (this.employee.isDirty()) {
            this.employee.revert();
        }

        if (this.payrollEmployee && this.payrollEmployee.isDirty()) {
            this.payrollEmployee.revert();
        }

        if (this.salary && this.salary.isDirty()) {
            this.salary.revert();
        }

        if (this.bankAccount && this.bankAccount.isDirty()) {
            this.bankAccount.revert();
        }

        this.closeDialog();
    }

    async onSaveSalary(salary: Salary, form: BaseForm): Promise<void> {
        this.isLoading = true;
        salary.employeeId = this.employee.id;
        if (salary.vacationPayPercentage === undefined) {
            salary.vacationPayPercentage = null;
        }
        const savePromises: Promise<Salary | Employee>[] = [
            salary.param('company', this.auth.company.id).param('employee', this.employee.id).save(),
        ];

        // @jira ENG-3263 concurrency issues with multiple saves
        // Only save the employee if we need to update currency
        // if they are on payroll there is no changes to currency
        if (!this.employee.isPayrollSyncEnabled) {
            savePromises.unshift(this.employee.param('company', this.auth.company.id).save());
        }

        try {
            await Promise.all(savePromises);
            this.notify.success('saved');
            form.reset();
            await this.reResolve();
        } catch (error) {
            this.notify.error(ErrorParser.parse(error || 'employees.jobAndPay.unableToAddSalary'));
        }
    }

    /**
     * @deprecated This function is for saving legacy tax credits.
     * The new tax credit system saves new tax credits within the tax-credit-table component
     */
    onSaveLegacyTaxCredit(taxCredit: LegacyTaxCredit): void {
        this.payrollEmployee.taxInformation.taxCredits.push(taxCredit.formData);
        this.onSave();
    }

    async onSaveAdditionalIncomeType(employeeAdditionalIncome: EmployeeAdditionalIncome): Promise<void> {
        this.isLoading = true;
        this.resetAdditionalIncomeModal = true;
        try {
            employeeAdditionalIncome.active = true;
            await employeeAdditionalIncome.save();
            employeeAdditionalIncome.param('employee', this.employee.id);
            this.notify.info('employees.jobAndPay.incomeTypeAdded');
        } catch (err) {
            this.notify.error('employees.jobAndPay.couldNotAddIncomeType');
        } finally {
            this.activeCompanyAdditionalIncomes.forEach(({ employeeAdditionalIncomes }) =>
                employeeAdditionalIncomes[0].revert()
            );
            this.isLoading = false;
            this.resetAdditionalIncomeModal = false;
        }
    }

    onCloseAdditionalIncomeEdit(): void {
        this.activeCompanyAdditionalIncomes.forEach(({ employeeAdditionalIncomes }) => {
            const employeeAdditionalIncome: EmployeeAdditionalIncome = employeeAdditionalIncomes[0];
            if (employeeAdditionalIncome.isDirty() && employeeAdditionalIncome.isPersisted) {
                employeeAdditionalIncome.save();
            }
        });
    }

    onDeleteSalary(salary: Salary): void {
        this.confirmDeleteSalary.show().then((res) => {
            if (!res) {
                return;
            }

            this.isLoading = true;
            salary
                .param('company', this.auth.company.id)
                .param('employee', this.employee.id)
                .delete()
                .then(() => {
                    this.reResolve();
                    this.notify.success('deleted');
                })
                .catch((err) => {
                    this.isLoading = false;
                    this.notify.error(err.message || 'employees.jobAndPay.unableToDeleteSalary');
                });
        });
    }

    /**
     * @deprecated This function is for deleting legacy tax credits.
     * The new tax credit system allows for deactivation of tax credits within the tax-credit-table component
     */
    onDeleteLegacyTaxCredit(taxCreditToDelete: LegacyTaxCredit): void {
        this.confirmDeleteTaxCredit.show().then((res) => {
            if (!res) {
                return;
            }

            const index = this.payrollEmployee.taxInformation.taxCredits.findIndex(
                (taxCredit) => taxCreditToDelete.name === taxCredit.name
            );

            if (index >= 0) {
                this.payrollEmployee.taxInformation.taxCredits.splice(index, 1);
                this.onSave();
            }
        });
    }

    setManager(): void {}

    determineIfEmployeeCanBeAddedToPayroll(employee: Employee): boolean {
        return Boolean(employee?.sin !== null && employee?.office !== null && !employee.isOnboardingPending);
    }

    addEmployeeToPayroll(): void {
        if (this.cannotAddToPayroll) {
            return;
        }

        const employeeCanBeAddedToPayroll = this.determineIfEmployeeCanBeAddedToPayroll(this.employee);
        if (!employeeCanBeAddedToPayroll) {
            this.notify.error('employees.jobAndPay.cannotSyncEmployeesToPayroll');
            return;
        }
        this.confirmEnablePayroll.show().then((res) => {
            if (!res) {
                return;
            }
            this.employee.isPayrollSyncEnabled = true;
            this.employee.isSinRequired = true;
            this.onSave();

            this.analyticsService.trackEvent(
                AnalyticEvents.EmployeeProfileJobAndPayTabEmploymentInformationAddToPayroll
            );
        });
    }

    editSalary(salary: Salary): void {
        if (salary.vacationPayPercentage !== null && salary.vacationPayPercentage.toString().includes('.00')) {
            salary.vacationPayPercentage = Math.floor(salary.vacationPayPercentage);
        }
        this.formEditSalary.isEditMode = true;
        this.formEditSalary.salary = salary;
        this.editSalaryModal.show();
    }

    navigateToCreateCompensation(): void {
        this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileJobAndPayTabHistoricalCompensationCreateClick);
        this.router.navigate(['/employees/directory/' + this.employee.id + '/create-compensation']);
    }

    async getPendingPayrollInformation(): Promise<PendingPayrollStatus> {
        const pendingPayrollInformation = await this.payrollService.fetchPendingPayrollStatus();
        return pendingPayrollInformation;
    }

    async determineIfCompensationIsOnAPendingPayroll(compensation: Salary): Promise<boolean> {
        const pendingPayrollInformation = await this.getPendingPayrollInformation();
        const compensationStartsAfterPendingPayroll = moment(compensation.effectiveAt).isAfter(
            pendingPayrollInformation.endDate
        );
        if (compensationStartsAfterPendingPayroll) {
            return false;
        }
        if (compensation.endsAt === null) {
            return moment(compensation.effectiveAt).isSameOrBefore(pendingPayrollInformation.endDate);
        }
        const compensationEndsBeforePendingPayrollStarts = moment(compensation.endsAt).isBefore(
            pendingPayrollInformation.startDate
        );
        if (compensationEndsBeforePendingPayrollStarts) {
            return false;
        }
        const isCompensationEndDateSameOrAfterPendingPayrollEndDate = moment(compensation.endsAt).isSameOrAfter(
            pendingPayrollInformation.endDate
        );
        if (isCompensationEndDateSameOrAfterPendingPayrollEndDate) {
            return moment(compensation.effectiveAt).isSameOrBefore(pendingPayrollInformation.endDate);
        }
        const compensationStartsBeforePendingPayrollButEndsDuringPendingPayroll =
            moment(compensation.effectiveAt).isSameOrBefore(pendingPayrollInformation.startDate) &&
            moment(compensation.endsAt).isSameOrBefore(pendingPayrollInformation.endDate);
        if (compensationStartsBeforePendingPayrollButEndsDuringPendingPayroll) {
            return true;
        }
        return moment(pendingPayrollInformation.startDate).isSameOrBefore(compensation.effectiveAt);
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        const salaryData = data as Salary;
        switch (menuItem.event) {
            case 'delete':
                if (salaryData.isPaid) {
                    this.notify.error('employees.jobAndPay.salaryHasBeenPaidCannotBeDeleted');
                    return;
                }
                if (this.pendingPayrollExists) {
                    const isCompensationOnAPendingPayroll = this.determineIfCompensationIsOnAPendingPayroll(salaryData);
                    if (isCompensationOnAPendingPayroll) {
                        this.notify.error('employees.jobAndPay.salaryIsPendingCannotBeDeleted');
                        return;
                    }
                }
                this.onDeleteCompensation(salaryData);
                break;
            case 'edit':
                this.router.navigate([
                    '/employees/directory/' + this.employee.id + '/edit-compensation/' + salaryData.id,
                ]);
                break;
        }
    }

    async onDeleteCompensation(compensation: Salary): Promise<void> {
        const deleteCompensationResponse = await this.confirmDeleteSalary.show();
        if (!deleteCompensationResponse) {
            this.isLoading = false;
            return;
        }
        try {
            this.isLoading = true;
            this.isCurrentCompensationsLoaded = false;
            await compensation.param('company', this.auth.company.id).param('employee', this.employee.id).delete();
            this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileJobAndPayTabHistoricalCompensationDelete);
            await this.reResolve();
            this.notify.success('employees.jobAndPay.compensationHasBeenDeleted');
        } catch (err) {
            this.isLoading = false;
            this.notify.error(err.message || 'employee.jobAndPay.unableToDeleteCompensation');
        }
    }

    protected setResolver(): void {
        this.resolver = this.injector.get(EmployeesResolver);
    }

    private async setDataValues(isReset = false): Promise<void> {
        if (this.auth.can(DataValue.permission.view) || this.isUserProfile()) {
            try {
                [this.dataValues] = await DataValue.param('company', this.auth.company.id)
                    .where('employee_id', this.employee.id)
                    .with('dataField')
                    .all();
                // If the data values are being reset due to a form submission the component needs to be signaled to re-assign values
                if (isReset) {
                    this.dataFieldGroupsComponents?.forEach((component) => component.resetDataValues());
                    this.dataFieldGroupValuesComponents?.forEach((component) => component.resetDataValues());
                }
            } catch (error) {
                this.notify.error(ErrorParser.parse(error));
            }
        }
    }

    private async setDataFields(): Promise<void> {
        // Want to get the datafields from the company endpoint and not an include so that they are filtered by permissible
        try {
            [this.dataFields] = await DataField.param('company', this.auth.employee.companyId)
                .with(['dataFieldGroup', 'dataFieldOptions'])
                .where('employeeIds', [this.employee.id])
                .orderBy('orderBy', 'ASC')
                .all();
            this.editableDataFields = this.dataFields.filter((dataField) => dataField.editable);
        } catch (error) {
            this.notify.error(ErrorParser.parse(error));
        }
    }

    private setCanEditDataValues(): void {
        this.canEditDataValues = this.auth.hasManageAccess(this.employee) && this.editableDataFields?.length > 0;
    }

    private async getPayrollInformation(): Promise<void> {
        if (this.auth.can(Platform.payroll.permission.view) && this.employee.activeOnPayroll()) {
            try {
                this.payrollEmployee = await PayrollEmployee.find(this.employee.id);
                await this.checkPendingPayrollStatus();
                await this.setAdditionalIncomes();
            } catch (error) {
                this.notify.error(ErrorParser.parse(error));
            }
        }
    }

    private async checkPendingPayrollStatus(): Promise<void> {
        try {
            const pendingPayrollInfo = await this.payrollService.fetchPendingPayrollStatus();
            this.pendingPayrollExists = pendingPayrollInfo.exists;
        } catch (error) {
            this.notify.error(ErrorParser.parse(error));
        }
    }

    private async setAdditionalIncomes(): Promise<void> {
        const [companyAdditionalIncomes] = await CompanyAdditionalIncome.param('company', this.auth.company.id)
            .where('active', true)
            .get();

        this.activeCompanyAdditionalIncomes = companyAdditionalIncomes;

        let [employeeAdditionalIncomes] = await EmployeeAdditionalIncome.param('employee', this.employee.id).get();

        employeeAdditionalIncomes = employeeAdditionalIncomes.filter(
            (employeeAdditionalIncome: EmployeeAdditionalIncome) => employeeAdditionalIncome?.frequency === 'Recurring'
        );

        this.activeCompanyAdditionalIncomes.forEach((companyAdditionalIncome: CompanyAdditionalIncome) => {
            let employeeAdditionalIncome = employeeAdditionalIncomes.find(
                ({ companyAdditionalIncomeId }) => companyAdditionalIncomeId === companyAdditionalIncome.id
            );

            if (!employeeAdditionalIncome) {
                employeeAdditionalIncome = new EmployeeAdditionalIncome();
                employeeAdditionalIncome.companyAdditionalIncomeId = companyAdditionalIncome.id;
                employeeAdditionalIncome.active = false;
            }

            employeeAdditionalIncome.param('employee', this.employee.id);
            companyAdditionalIncome.employeeAdditionalIncomes = [employeeAdditionalIncome];
        });
    }

    private async setSalary(): Promise<void> {
        if (this.auth.can(Salary.permission.view) || this.isUserProfile()) {
            try {
                const [salaries] = await Salary.param('company', this.auth.company.id)
                    .param('employee', this.employee.id)
                    .orderBy('effectiveAt', 'DESC')
                    .all();
                this.employee.salaries = salaries;
                this.salary = this.employee.salary;
                this.setCanEditSalary();
                this.setCanDeleteSalary();
                this.setCompensationTable(true);
            } catch (error) {
                this.notify.error(ErrorParser.parse(error));
            }
        }
    }

    private setCompensationTable(displayVacationPay: boolean): void {
        this.table = new CompensationHistoryTable(
            this.auth,
            this.employee,
            this.canEditSalary,
            this.canDeleteSalary,
            displayVacationPay
        );
        this.readOnlyTable = new CompensationHistoryReadOnlyTable(this.auth, this.employee, displayVacationPay);
        this.displayCompensationTable = true;
    }

    private async setBankAccount(): Promise<void> {
        this.isBankAccountLoaded = false;

        if (this.auth.can(BankAccount.permission.some('view')) || this.isUserProfile()) {
            this.bankAccount = this.employee.bankAccount || new BankAccount();
            if (this.employee.bankAccountId) {
                try {
                    const bankAccount = await BankAccount.param('company', this.auth.company.id)
                        .param('employee', this.employee.id)
                        .orderBy('effective_at', 'DESC')
                        .find(this.employee.bankAccountId);
                    this.employee.bankAccounts = [bankAccount];
                    this.bankAccount = this.employee.bankAccount;
                } catch (error) {
                    this.notify.error(ErrorParser.parse(error));
                }
            }
        }

        this.isBankAccountLoaded = true;
    }

    private async setOffices(): Promise<void> {
        Office.param('company', this.employee.companyId)
            .all()
            .then(([offices]) => (this.offices = offices));
    }

    private async getChangeRequestTemplates(): Promise<void> {
        this.changeRequestTemplates = await this.changeRequestTemplateService.getActiveTemplates();
        this.canCreateChangeRequest =
            this.changeRequestTemplateService.canRequestForEmployee(this.employee) &&
            this.changeRequestTemplates.length > 0;
    }

    private async setOfficesAndBankAccount(): Promise<void> {
        const retrievedBankInformation = this.setBankAccount();
        const retrievedOffices = this.setOffices();

        Promise.all([retrievedBankInformation, retrievedOffices]).then(() => this.setJobAndPayWarningProvider());
    }

    private setJobAndPayWarningProvider(): void {
        this.jobAndPayWarningProvider = new JobAndPayWarningProvider(
            this.employee,
            this.bankAccount,
            this.offices,
            this.isQcSupported
        );
    }

    private setCanDeleteSalary(): boolean {
        const isSelf = this.employee.id === this.auth.employee.id;

        // You can only delete salaries for yourself if you are an admin
        if (isSelf) {
            this.canDeleteSalary = this.auth.isAdmin();

            return;
        }

        const moreThanOne = this.employee.salaries.length > 1;
        const hasPermission = this.auth.can(Salary.permission.some('delete'));

        this.canDeleteSalary = moreThanOne && hasPermission;
    }

    private setCanEditSalary(): boolean {
        const isSelf = this.employee.id === this.auth.employee.id;

        if (isSelf) {
            // You can only create salaries for yourself if you are an admin
            this.canEditSalary = this.auth.isAdmin();

            return;
        }

        const hasPermission = this.auth.can(Salary.permission.some('create'));

        this.canEditSalary = hasPermission;
    }

    private setHasEditVacationPayMethodPermission(): boolean {
        const isSelf = this.employee.id === this.auth.employee.id;
        const hasPermission = this.auth.can(Salary.permission.some('view', 'create', 'search'));
        return isSelf ? this.auth.isAdmin() : hasPermission;
    }

    private addPendoTrackingForEmployemntInformationModal(): void {
        if (this.employee.isFieldDirty('paymentMethod')) {
            this.isSelfEmployeeView
                ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileJobAndPayTabEditBankAccount)
                : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileJobAndPayTabBankAccountSave);
        }

        if (this.employee.isFieldDirty('hiredAt')) {
            this.analyticsService.trackEvent(
                AnalyticEvents.EmployeeProfileJobAndPayTabEmploymentInformationHireDateSave
            );
        }

        if (this.employee.isFieldDirty('manager')) {
            this.analyticsService.trackEvent(
                AnalyticEvents.EmployeeProfileJobAndPayTabEmploymentInformationReportsToSave
            );
        }

        if (this.employee.isFieldDirty('departmentId')) {
            this.analyticsService.trackEvent(
                AnalyticEvents.EmployeeProfileJobAndPayTabEmploymentInformationDepartmentSave
            );
        }

        if (this.employee.isFieldDirty('jobId')) {
            this.analyticsService.trackEvent(
                AnalyticEvents.EmployeeProfileJobAndPayTabEmploymentInformationPositionSave
            );
        }

        if (this.employee.isFieldDirty('officeId')) {
            this.analyticsService.trackEvent(
                AnalyticEvents.EmployeeProfileJobAndPayTabEmploymentInformationOfficeNameSave
            );
        }

        if (this.employee.isFieldDirty('employmentType')) {
            this.analyticsService.trackEvent(
                AnalyticEvents.EmployeeProfileJobAndPayTabEmploymentInformationEmploymentTypeSave
            );
        }

        if (this.employee.isFieldDirty('stockOptions')) {
            this.analyticsService.trackEvent(
                AnalyticEvents.EmployeeProfileJobAndPayTabEmploymentInformationStockOptionsSave
            );
        }

        if (this.employee.isFieldDirty('additionalTerms')) {
            this.analyticsService.trackEvent(
                AnalyticEvents.EmployeeProfileJobAndPayTabEmploymentInformationAdditionalTermsSave
            );
        }
    }

    private canShowEditModalForDefaultGroup(groupName: string): boolean {
        if (this.auth.can(this.editEmploymentPermission)) {
            return true;
        }

        return (this.editableDataFields ?? []).some((df: DataField) => df.dataFieldGroup.name === groupName);
    }
}
